import { Hbar, PublicKey, TokenAssociateTransaction, Transaction } from '@hashgraph/sdk';
import { proto } from '@hashgraph/proto';
import { Buffer } from 'buffer';

/**
 * Owners sign a proposal by schedule ID (or, for token associations, by the
 * stored frozen bytes), and the app shows them the description and params
 * the proposer typed. A schedule that does something else, worst case a
 * transfer out of the signer's own account, would execute on their
 * signature. These checks run in the signer's browser against the mirror
 * node's copy, so they also cover proposals the backend never checked.
 *
 * The rules mirror the backend's (hedera-multisig-backend
 * src/wallet/transaction/schedule-verifier.ts). This proto version predates
 * HIP-1195 hook fields and skips them when decoding, so every decoded body
 * must re-encode to the exact bytes it came from: a field this version can't
 * read is a refusal, not a silent skip.
 */

export interface MirrorSchedule {
  payer_account_id: string;
  deleted: boolean;
  executed_timestamp: string | null;
  transaction_body?: string; // base64 SchedulableTransactionBody
}

export interface TokenInfo {
  decimals: string | number;
  symbol: string;
  name?: string;
}

/** An account key as the mirror node reports it. */
export interface MirrorKey {
  _type: string;
  key: string;
}

/** A key as the signatures it needs and the public keys (DER) that can give them. */
export interface ThresholdKey {
  threshold: number;
  keys: string[];
  /** False for a bare public key, which is an ordinary account, not a multisig */
  isList: boolean;
}

export interface ProposalToCheck {
  type: string;
  walletAccountId: string;
  params: any;
  /** TokenTransfer: the token as the mirror reports it */
  token?: TokenInfo;
  /** ThresholdUpdate: the wallet's key on Hedera now */
  walletKey?: ThresholdKey;
  /** ThresholdUpdate: each listed signer account's own key on Hedera (DER), null when unreadable */
  signerKeys?: Record<string, string | null>;
}

/** A row for the signer to read, built from what the schedule really does. */
export type DetailItem = { label: string; value: string };

export type Verdict =
  | { ok: true; items: DetailItem[] }
  | { ok: false; reason: string; alreadyExecuted?: boolean };

const fail = (reason: string, alreadyExecuted = false): Verdict => ({
  ok: false,
  reason,
  alreadyExecuted,
});

/**
 * An entity ID as "shard.realm.num" without leading zeros, or null when `id`
 * isn't one. "0.0.06002" is 0.0.6002 on Hedera, so IDs are compared this way.
 *
 * Each part is at most 19 digits. Past that, Hedera stops reading the number
 * as decimal: the SDK takes 20 or more digits as an alias, and the mirror
 * node takes 40 as an EVM address, so "0.0.000…2184" would be account
 * 0.0.8580 there but 0.0.2184 here.
 */
export const canonicalId = (id: unknown): string | null => {
  const match = typeof id === 'string' ? /^(\d{1,19})\.(\d{1,19})\.(\d{1,19})$/.exec(id) : null;
  return match ? match.slice(1).map((n) => BigInt(n).toString()).join('.') : null;
};

/** "shard.realm.num", or null for an alias or a missing ID, which never matches. */
const entityId = (
  id: { shardNum?: any; realmNum?: any } | null | undefined,
  num: any,
): string | null => (id && num != null ? `${id.shardNum ?? 0}.${id.realmNum ?? 0}.${num}` : null);

// An ID may carry its number and an alias (or EVM address) at once. Hedera
// uses the alias, the last of the two on the wire, while the number reads
// as 0.0.0 here, so such an ID matches nothing.
const accountId = (id?: proto.IAccountID | null) =>
  id?.alias?.length ? null : entityId(id, id?.accountNum);
const contractId = (id?: proto.IContractID | null) =>
  id?.evmAddress?.length ? null : entityId(id, id?.contractNum);
const tokenId = (id?: proto.ITokenID | null) => entityId(id, id?.tokenNum);

/** Names of the fields `message` sets beyond `allowed`. */
const extraFields = (message: object, allowed: string[]): string[] =>
  Object.entries(message)
    .filter(([field]) => !allowed.includes(field))
    .filter(([, value]) => value != null && !(Array.isArray(value) && value.length === 0))
    .map(([field]) => field);

/** True when `encoded`, the re-encoding of what was decoded from `raw`, is `raw` exactly. */
const reencodes = (encoded: Uint8Array, raw: Uint8Array): boolean =>
  Buffer.from(encoded).equals(Buffer.from(raw));

const isAmount = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value > 0;

const tinybars = (hbar: number | string): string => new Hbar(hbar).toTinybars().toString();

/** Raw token units for a display amount, the same string math the proposal flow uses. */
const rawTokenAmount = (amount: number, decimals: number): string => {
  const plain = amount.toLocaleString('en-US', { useGrouping: false, maximumFractionDigits: 20 });
  const [intPart, decPart = ''] = plain.split('.');
  return BigInt(intPart + decPart.padEnd(decimals, '0').slice(0, decimals)).toString();
};

/** A raw token amount as a display string, e.g. "1234567" at 2 decimals → "12345.67". */
const formatTokenAmount = (raw: string, decimals: number): string => {
  if (decimals === 0) return raw;
  const padded = raw.padStart(decimals + 1, '0');
  const whole = padded.slice(0, -decimals);
  const fraction = padded.slice(-decimals).replace(/0+$/, '');
  return fraction ? `${whole}.${fraction}` : whole;
};

const normalizePublicKey = (key: string): string => PublicKey.fromString(key).toStringDer();

/** Same keys in any order or encoding; a list that repeats a key never matches. */
const isSameKeySet = (a: string[], b: string[]): boolean => {
  const left = a.map(normalizePublicKey);
  const right = b.map(normalizePublicKey);
  const leftSet = new Set(left);
  const rightSet = new Set(right);
  return (
    leftSet.size === left.length &&
    rightSet.size === right.length &&
    leftSet.size === rightSet.size &&
    Array.from(leftSet).every((k) => rightSet.has(k))
  );
};

/** A decoded key list as a ThresholdKey; throws for a bare key, nested lists, contract keys, repeats. */
const thresholdKeyFromProto = (key: proto.IKey): ThresholdKey => {
  const toDer = (k: proto.IKey): string => {
    if (k.ed25519) return PublicKey.fromBytesED25519(k.ed25519).toStringDer();
    if (k.ECDSASecp256k1) return PublicKey.fromBytesECDSA(k.ECDSASecp256k1).toStringDer();
    throw new Error('unsupported key');
  };
  let threshold: number;
  let entries: proto.IKey[];
  if (key.thresholdKey) {
    threshold = key.thresholdKey.threshold ?? 0;
    entries = key.thresholdKey.keys?.keys ?? [];
  } else if (key.keyList) {
    entries = key.keyList.keys ?? [];
    threshold = entries.length;
  } else {
    // The app always sets a threshold list; a bare key would make the wallet one account's
    throw new Error('not a key list');
  }
  const keys = entries.map(toDer);
  if (!keys.length || threshold < 1 || threshold > keys.length) throw new Error('invalid threshold');
  // Hedera counts a repeated key once per entry: 2-of-[A, A, B] is A's alone
  if (new Set(keys).size !== keys.length) throw new Error('repeated key');
  return { threshold, keys, isList: true };
};

/** A mirror-node account key as a ThresholdKey; throws for key types this app doesn't support. */
export const parseMirrorKey = (key: MirrorKey): ThresholdKey => {
  switch (key?._type?.toUpperCase()) {
    case 'ED25519':
      return { threshold: 1, keys: [PublicKey.fromStringED25519(key.key).toStringDer()], isList: false };
    case 'ECDSA_SECP256K1':
      return { threshold: 1, keys: [PublicKey.fromStringECDSA(key.key).toStringDer()], isList: false };
    case 'PROTOBUFENCODED':
      return thresholdKeyFromProto(proto.Key.decode(Buffer.from(key.key, 'hex')));
    default:
      throw new Error('unsupported key');
  }
};

/** Exactly two entries: the wallet sends `amount`, `to` receives it, nothing else. */
const checkTransferPair = (
  amounts: proto.IAccountAmount[],
  from: string,
  to: string,
  amount: string,
): string | null => {
  if (amounts.length !== 2) return 'Schedule must have exactly one sender and one recipient';
  if (amounts.some((aa) => aa.isApproval)) return 'Schedule must not use an allowance';
  const extra = amounts.flatMap((aa) => extraFields(aa, ['accountID', 'amount', 'isApproval']));
  if (extra.length) return `Schedule also sets ${Array.from(new Set(extra)).join(', ')}`;
  const sent = amounts.find((aa) => aa.amount?.toString() === `-${amount}`);
  const received = amounts.find((aa) => aa.amount?.toString() === amount);
  if (!sent || !received) return 'Schedule moves a different amount than the proposal';
  if (accountId(sent.accountID) !== from) return 'Schedule debits an account other than the wallet';
  if (accountId(received.accountID) !== to) return 'Schedule pays a different recipient than the proposal';
  return null;
};

const checkHbarTransfer = (
  body: proto.SchedulableTransactionBody,
  wallet: string,
  params: any,
): string | DetailItem[] => {
  const to = canonicalId(params.toAccountId);
  if (!to) return 'Proposal names no valid recipient';
  if (!isAmount(params.amount)) return 'Proposal states no valid amount';
  let amount: string;
  try {
    amount = tinybars(params.amount);
  } catch {
    return 'Proposal states no valid amount';
  }
  if (body.data !== 'cryptoTransfer' || !body.cryptoTransfer) return 'Schedule is not an HBAR transfer';
  if (body.cryptoTransfer.tokenTransfers?.length) return 'Schedule also moves tokens';
  const reason = checkTransferPair(body.cryptoTransfer.transfers?.accountAmounts ?? [], wallet, to, amount);
  if (reason) return reason;
  return [
    { label: 'Action', value: 'Send HBAR' },
    { label: 'From', value: wallet },
    { label: 'To', value: to },
    { label: 'Amount', value: Hbar.fromTinybars(amount).toString() },
  ];
};

const checkTokenTransfer = (
  body: proto.SchedulableTransactionBody,
  wallet: string,
  params: any,
  token?: TokenInfo,
): string | DetailItem[] => {
  const id = canonicalId(params.tokenId);
  const to = canonicalId(params.toAccountId);
  if (!id) return 'Proposal names no valid token';
  if (!to) return 'Proposal names no valid recipient';
  if (!isAmount(params.amount)) return 'Proposal states no valid amount';
  if (!token) return 'Schedule cannot be checked without the token details';
  const decimals = Number(token.decimals);
  if (params.tokenDecimals !== decimals) {
    return `Proposal uses ${params.tokenDecimals} decimals, but token ${id} has ${decimals}`;
  }
  if (params.tokenSymbol != null && params.tokenSymbol !== token.symbol) {
    return `Proposal names the token ${params.tokenSymbol}, but token ${id} is ${token.symbol}`;
  }
  if (body.data !== 'cryptoTransfer' || !body.cryptoTransfer) return 'Schedule is not a token transfer';
  if (body.cryptoTransfer.transfers?.accountAmounts?.length) return 'Schedule also moves HBAR';
  const lists = body.cryptoTransfer.tokenTransfers ?? [];
  if (lists.length !== 1) return 'Schedule must move exactly one token';
  const [list] = lists;
  const extra = extraFields(list, ['token', 'transfers', 'nftTransfers', 'expectedDecimals']);
  if (extra.length) return `Schedule also sets ${extra.join(', ')}`;
  if (tokenId(list.token) !== id) return 'Schedule moves a different token than the proposal';
  if (list.nftTransfers?.length) return 'Schedule moves NFTs';
  const raw = rawTokenAmount(params.amount, decimals);
  const reason = checkTransferPair(list.transfers ?? [], wallet, to, raw);
  if (reason) return reason;
  return [
    { label: 'Action', value: 'Send tokens' },
    { label: 'From', value: wallet },
    { label: 'To', value: to },
    { label: 'Amount', value: `${formatTokenAmount(raw, decimals)} ${token.symbol}` },
    { label: 'Token', value: id },
  ];
};

const isPayable = (value: unknown): boolean =>
  value == null ||
  value === '' ||
  (typeof value === 'number' && Number.isFinite(value) && value >= 0) ||
  (typeof value === 'string' && /^\d+(\.\d+)?$/.test(value));

const checkContractCall = (body: proto.SchedulableTransactionBody, params: any): string | DetailItem[] => {
  const contract = canonicalId(params.contractId);
  if (!contract) return 'Proposal names no valid contract';
  if (!isPayable(params.payableAmount)) return 'Proposal states no valid HBAR amount for the call';
  if (params.gas == null) return 'Proposal states no gas limit';
  if (body.data !== 'contractCall' || !body.contractCall) return 'Schedule is not a contract call';
  const call = body.contractCall;
  if (contractId(call.contractID) !== contract) {
    return 'Schedule calls a different contract than the proposal';
  }
  const payable = call.amount?.toString() ?? '0';
  if (payable !== tinybars(params.payableAmount || 0)) {
    return 'Schedule sends a different HBAR amount than the proposal';
  }
  if (call.gas?.toString() !== String(params.gas)) return 'Schedule sets a different gas limit than the proposal';
  const data = Buffer.from(call.functionParameters ?? []);
  return [
    { label: 'Action', value: 'Call a contract' },
    { label: 'Contract', value: contract },
    // The name is the proposer's: only the call data below is what executes
    { label: 'Method (not verified)', value: String(params.contractMethod ?? '?') },
    {
      label: 'Call data',
      value: data.length ? `0x${data.subarray(0, 4).toString('hex')}…, ${data.length} bytes` : 'none',
    },
    { label: 'Gas limit', value: call.gas.toString() },
    { label: 'Sends', value: Hbar.fromTinybars(payable).toString() },
  ];
};

const checkKeyUpdate = (
  body: proto.SchedulableTransactionBody,
  wallet: string,
  params: any,
  walletKey?: ThresholdKey,
  signerKeys?: Record<string, string | null>,
): string | DetailItem[] => {
  if (body.data !== 'cryptoUpdateAccount' || !body.cryptoUpdateAccount) return 'Schedule is not an account update';
  const update = body.cryptoUpdateAccount;
  if (accountId(update.accountIDToUpdate) !== wallet) return 'Schedule updates an account other than the wallet';
  const extra = extraFields(update, ['accountIDToUpdate', 'key']);
  if (extra.length) return `Schedule also changes ${extra.join(', ')}`;
  if (!update.key) return 'Schedule does not change the key';
  let key: ThresholdKey;
  try {
    key = thresholdKeyFromProto(update.key);
  } catch {
    return 'Schedule sets a key this app does not support';
  }

  // The UI names signers by account, so each listed key must be its account's
  // own key on Hedera: otherwise "Carol" could carry someone else's key.
  const signers: any[] = Array.isArray(params.newSigners) ? params.newSigners : [];
  const rows: string[] = [];
  for (const signer of signers) {
    const id = canonicalId(signer?.accountId);
    if (!id) return 'Proposal lists a signer without a valid account ID';
    const label = signer.name ? `${signer.name} (${id})` : id;
    const onChain = signerKeys?.[id];
    if (onChain == null) return `Could not read the key of ${label} from the mirror node`;
    let listed: string | null = null;
    try {
      listed = normalizePublicKey(signer.publicKey);
    } catch {
      // a listed key that doesn't parse can't match
    }
    if (listed !== onChain) {
      // Still in the wallet: the account has changed its key since the proposal
      return listed && walletKey?.keys.includes(listed)
        ? `Proposal uses the old key of ${label}, who has changed it since. Ask for the update to be proposed again`
        : `Proposal lists ${label} with a key that is not theirs on Hedera`;
    }
    rows.push(label);
  }

  let same = false;
  try {
    same =
      key.threshold === params.newThreshold &&
      isSameKeySet(
        signers.map((s) => s.publicKey),
        key.keys,
      );
  } catch {
    // a proposed key that doesn't parse can't match
  }
  if (!same) return 'Schedule sets a different key than the proposal';
  return [
    { label: 'Action', value: 'Change the wallet key' },
    { label: 'Wallet', value: wallet },
    { label: 'New key', value: `${key.threshold} of ${key.keys.length} signers` },
    { label: 'Signers', value: rows.join(', ') },
  ];
};

/**
 * Whether `schedule` does what `proposal` says. On success, the rows to show
 * the signer come from the decoded schedule, not from the proposal's text.
 */
export const checkScheduledProposal = (schedule: MirrorSchedule, proposal: ProposalToCheck): Verdict => {
  if (schedule.deleted) return fail('Schedule has been deleted');
  if (schedule.executed_timestamp) return fail('Schedule has already executed', true);
  if (schedule.payer_account_id !== proposal.walletAccountId) {
    return fail('Schedule is paid for by an account other than the wallet');
  }
  let result: string | DetailItem[];
  try {
    const raw = Buffer.from(schedule.transaction_body ?? '', 'base64');
    const body = proto.SchedulableTransactionBody.decode(raw);
    if (!reencodes(proto.SchedulableTransactionBody.encode(body).finish(), raw)) {
      return fail('Schedule contains fields this app cannot read');
    }
    const { type, walletAccountId, params, token, walletKey, signerKeys } = proposal;
    switch (type) {
      case 'Transfer':
        result = checkHbarTransfer(body, walletAccountId, params);
        break;
      case 'TokenTransfer':
        result = checkTokenTransfer(body, walletAccountId, params, token);
        break;
      case 'ContractInteraction':
        result = checkContractCall(body, params);
        break;
      case 'ThresholdUpdate':
        result = checkKeyUpdate(body, walletAccountId, params, walletKey, signerKeys);
        break;
      default:
        result = `Proposal type ${type} cannot be checked`;
    }
  } catch {
    result = 'Schedule could not be decoded';
  }
  return typeof result === 'string' ? fail(result) : { ok: true, items: result };
};

/** Whether every body in serialized transaction bytes re-encodes exactly. */
const bodiesReencode = (raw: Uint8Array): boolean => {
  let list = proto.TransactionList.decode(raw).transactionList;
  if (!list.length) list = [proto.Transaction.decode(raw)];
  return list.every((tx) => {
    const bodyBytes = tx.signedTransactionBytes?.length
      ? proto.SignedTransaction.decode(tx.signedTransactionBytes).bodyBytes
      : tx.bodyBytes;
    if (!bodyBytes?.length) return false;
    return reencodes(proto.TransactionBody.encode(proto.TransactionBody.decode(bodyBytes)).finish(), bodyBytes);
  });
};

/**
 * Token association isn't schedulable: owners sign the stored frozen bytes
 * directly. Whether those bytes are exactly the proposed association.
 */
export const checkTokenAssociation = (
  frozenTxBytes: string,
  walletAccountId: string,
  params: { tokenId: string; tokenSymbol?: string; tokenName?: string },
  token?: TokenInfo,
): Verdict => {
  const id = canonicalId(params?.tokenId);
  if (!id) return fail('Proposal names no valid token');
  let tx: Transaction;
  try {
    const raw = Buffer.from(frozenTxBytes ?? '', 'base64');
    if (!bodiesReencode(raw)) return fail('Transaction contains fields this app cannot read');
    tx = Transaction.fromBytes(raw);
  } catch {
    return fail('Transaction could not be decoded');
  }
  if (!(tx instanceof TokenAssociateTransaction)) return fail('Transaction is not a token association');
  if (tx.transactionId?.accountId?.toString() !== walletAccountId) {
    return fail('Transaction is paid for by an account other than the wallet');
  }
  if (tx.accountId?.toString() !== walletAccountId) {
    return fail('Transaction associates tokens with an account other than the wallet');
  }
  const tokens = (tx.tokenIds ?? []).map(String);
  if (tokens.length !== 1 || tokens[0] !== id) {
    return fail('Transaction associates different tokens than the proposal');
  }
  if (!token) return fail(`Could not read token ${id} from the mirror node`);
  if (params.tokenSymbol != null && params.tokenSymbol !== token.symbol) {
    return fail(`Token ${id} is ${token.symbol}, not ${params.tokenSymbol}`);
  }
  if (params.tokenName != null && params.tokenName !== token.name) {
    return fail(`Token ${id} is named ${token.name}, not ${params.tokenName}`);
  }
  return {
    ok: true,
    items: [
      { label: 'Action', value: 'Associate a token' },
      { label: 'Account', value: walletAccountId },
      { label: 'Token', value: `${token.symbol} (${id})` },
    ],
  };
};
